import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from "moment";
import {
  ALL_SPACES,
  MEASURE_RATING,
  MEASURE_RESET,
  STANDARD,
  STANDARD_RESET,
} from "./constants";
import { getFromServerTopic } from "../utility/slice";
import { IDLE, LOADING } from "../constants";
import { USEROPTION_IAQ_USE_AVERAGE } from "@hooks/useUserOptions";

const SLICE = "iaqTrends";

/*
 * Temporary flag to control whether we should use median iaqClass for devices.
 * I suspect we should always use the worst sensor IAQ evaluation for devices,
 * but if not this value should be set to true (or ideally, removed).
 */
const IS_USE_AVERAGE_FOR_DEVICES_TOO = false;

const action = (name) => `${SLICE}/${name}`;

const getFromServer = (action, contextOptionsFn, payloadFn) =>
  getFromServerTopic({ topic: SLICE, action, contextOptionsFn, payloadFn });

const sensorsField = (forBuilding) => {
  return forBuilding.standard === STANDARD_RESET ? "reset" : "iaq";
};

const getBuilding = createAsyncThunk(
  action("getBuilding"),
  async (payload, { getState }) => {
    const { lookupdata } = getState().persistedAppData;
    return lookupdata.buildings.find((it) => it.id === payload);
  },
);

const getUserOptions = createAsyncThunk(
  action("getUserOptions"),
  async (payload, { getState }) => {
    const { userinfo } = getState().persistedAppData;
    const { options } = userinfo;
    return options;
  },
);

const getEquipmentForBuilding = getFromServer(
  "getEquipmentForBuilding",
  async ({ arg }) => {
    return { site_id: arg };
  },
  async (arg) => arg,
);

const getIAQDataForBuildingAndCurrentMonth = getFromServer(
  "getIAQDataForBuildingAndMonth",
  async ({ arg }) => {
    return { site_id: arg.siteId, month: arg.month };
  },
  async (arg) => arg,
);

const changeMonth = (name, count) =>
  createAsyncThunk(action(name), async (arg, { dispatch, getState }) => {
    const month = getState()
      [SLICE].buildingData[arg].reportMonth.clone()
      .add(count, "month")
      .format("YYYY-MM");

    return dispatch(
      getIAQDataForBuildingAndCurrentMonth({
        siteId: arg,
        month,
      }),
    );
  });

export const nextMonth = changeMonth("nextMonth", 1);
export const previousMonth = changeMonth("previousMonth", -1);

export const initializePage = createAsyncThunk(
  action("initializePage"),
  async (arg, { dispatch }) => {
    return Promise.all([
      dispatch(getUserOptions(arg)),
      dispatch(getBuilding(arg)),
      dispatch(getEquipmentForBuilding(arg)),
      dispatch(
        getIAQDataForBuildingAndCurrentMonth({
          siteId: arg,
          month: moment().startOf("month").format("YYYY-MM"),
        }),
      ),
    ]);
  },
);

export const changeDeviceDay = getFromServer(
  "getEquipmentSensorDataForDay",
  async ({ payload }) => {
    return {
      equipment_id: payload.equipmentId,
      day: payload.day,
    };
  },
  async (arg) => {
    return {
      equipmentId: arg.deviceDay.id,
      day: arg.deviceDay.day.format("YYYY-MM-DD"),
    };
  },
);

const shiftDeviceDay = (name, dayFn) =>
  createAsyncThunk(action(name), async (arg, { dispatch, getState }) => {
    const deviceDay = getState()[SLICE].buildingData[arg].deviceDay;
    return dispatch(
      changeDeviceDay({
        siteId: arg,
        deviceDay: {
          ...deviceDay,
          day: dayFn(deviceDay.day.clone()),
        },
      }),
    );
  });

export const nextDeviceDay = shiftDeviceDay("nextDeviceDay", (day) =>
  day.add(1, "day"),
);

export const previousDeviceDay = shiftDeviceDay("previousDeviceDay", (day) =>
  day.subtract(1, "day"),
);

const stringToDateAllSpacesWeeks = (allSpacesWeeks) => {
  return allSpacesWeeks.map((w) =>
    w.map((d) => {
      return d ? { ...d, day: moment(d.day) } : undefined;
    }),
  );
};

/**
 * Sets the iaqClass field used by the UI based on how whether we're using "worst" or "average" strategy.
 * @param isUseAverage
 *  whether to use IAQ averages instead of worst
 * @param standard
 *  which IAQ standard to use
 * @param measure
 *  which measure to use
 * @param it
 * @returns {*|{iaqClass: (*|undefined)}|undefined}
 */
const setIAQClass = (isUseAverage, standard, measure, it) => {
  if (!it) return undefined;

  const measureObj =
    measure === MEASURE_RATING ? it : it?.sensorSpecific?.[measure];

  if (standard === STANDARD_RESET) {
    return {
      ...it,
      iaqClass: measureObj
        ? isUseAverage
          ? measureObj.reset?.median?.iaqClass
          : measureObj.reset?.worst?.iaqClass
        : "iaq_inactive",
    };
  }

  return {
    ...it,
    iaqClass: measureObj
      ? isUseAverage
        ? measureObj.iaq?.median?.iaqClass
        : measureObj.iaq?.worst?.iaqClass
      : "iaq_inactive",
  };
};

const setIAQClassAllSpacesWeeks = (isUseAverage, standard, measure, weeks) => {
  return weeks.map((w) =>
    w.map((d) => setIAQClass(isUseAverage, standard, measure, d)),
  );
};

const setIAQClassDeviceWeeks = (
  isUseAverage,
  standard,
  measure,
  deviceWeeks,
) => {
  return deviceWeeks.map(({ device, weeks }) => {
    return {
      device,
      weeks: weeks.map((w) =>
        w.map((d) => setIAQClass(isUseAverage, standard, measure, d)),
      ),
    };
  });
};

export const stringToDateDeviceWeeks = (deviceWeeks) => {
  const parsedDeviceWeeks = [];
  for (const deviceWeek of deviceWeeks) {
    if (!deviceWeek) {
      parsedDeviceWeeks.push(undefined);
      continue;
    }
    const parsedWeeks = [];
    for (const week of deviceWeek.weeks) {
      const parsedDays = [];
      for (const day of week) {
        if (!day) {
          parsedDays.push(undefined);
          continue;
        }
        parsedDays.push({
          ...day,
          day: moment(day.day),
        });
      }
      parsedWeeks.push(parsedDays);
    }
    parsedDeviceWeeks.push({ ...deviceWeek, weeks: parsedWeeks });
  }
  return parsedDeviceWeeks;
};

const updateForStandardAndMeasure = ({ state, siteId, standard, measure }) => {
  const forBuilding = state.buildingData[siteId];

  if (standard) {
    forBuilding.standard = standard;
  }
  if (measure) forBuilding.measure = measure;

  const { isUseAverage } = state;
  forBuilding.allSpacesWeeks = setIAQClassAllSpacesWeeks(
    isUseAverage,
    forBuilding.standard,
    forBuilding.measure,
    forBuilding.rawAllSpacesWeeks,
  );
  forBuilding.deviceWeeks = setIAQClassDeviceWeeks(
    IS_USE_AVERAGE_FOR_DEVICES_TOO && isUseAverage,
    forBuilding.standard,
    forBuilding.measure,
    forBuilding.rawDeviceWeeks,
  );
};

const updateDeviceDays = (forBuilding, day) => {
  forBuilding.devicesForDay = [];

  if (!day) return; // Navigating back to All Spaces month view

  DEVICE: for (const { device, weeks } of forBuilding.deviceWeeks) {
    if (device.equipment_template_id === 1) continue; // Skip bridges

    for (const week of weeks) {
      for (const dayEntry of week) {
        if (!dayEntry) continue;

        const { day: d, iaqClass } = dayEntry;
        const isMatchingDay = day.isSame(d);

        if (isMatchingDay) {
          forBuilding.devicesForDay.push({ ...device, iaqClass });
          continue DEVICE;
        }
      }
    }
  }
};

export const slice = createSlice({
  name: SLICE,
  initialState: {
    buildingData: {},
    isUseAverage: false,
    status: IDLE,
  },
  reducers: {
    changeDeviceDaySensor: (state, action) => {
      const { siteId, sensorName } = action.payload;
      const forBuilding = state.buildingData[siteId];
      forBuilding.deviceDay = {
        ...forBuilding.deviceDay,
        sensorName,
        sensorUnit:
          sensorName === "Lux"
            ? "lux"
            : forBuilding.sensors[sensorsField(forBuilding)].find(
                (it) => it.commonName === sensorName,
              ).sensorUnit,
      };
      forBuilding.sensorChartData = forBuilding.deviceDayData[sensorName];
    },
    changeMeasure: (state, action) => {
      const { siteId, measure } = action.payload;

      updateForStandardAndMeasure({
        state,
        siteId,
        measure,
      });
    },
    changeStandard: (state, action) => {
      const { siteId, standard } = action.payload;
      const forBuilding = state.buildingData[siteId];

      let measure = undefined;

      // Set to Rating if user switched to RESET standard and it doesn't support the sensor
      if (
        standard === STANDARD_RESET &&
        !!MEASURE_RESET.find((it) => it === forBuilding.measure)
      ) {
        measure = MEASURE_RATING;
      }

      updateForStandardAndMeasure({
        state,
        siteId,
        standard,
        measure,
      });

      if (forBuilding.viewDay) {
        updateDeviceDays(forBuilding, forBuilding.viewDay);
      }
    },
    changeView: (state, action) => {
      const { siteId, viewBy } = action.payload;
      const forBuilding = state.buildingData[siteId];
      forBuilding.viewBy = viewBy;
    },
    hideDeviceDay: (state, action) => {
      const forBuilding = state.buildingData[action.payload];
      forBuilding.deviceDay = undefined;
    },
    viewForDay: (state, action) => {
      const { day, siteId } = action.payload;
      const forBuilding = state.buildingData[siteId];
      forBuilding.viewDay = day;

      updateDeviceDays(forBuilding, day);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(changeDeviceDay.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { deviceDay, siteId } = meta.arg;
        const response = payload.response;
        const { legend, resetLegend, data } = response;

        const forBuilding = state.buildingData[siteId];
        const sf = sensorsField(forBuilding);

        const defaultLegendRow = Object.values(legend).find(
          (it) => it.sensorIAQs.length > 0,
        );
        forBuilding.deviceDay = deviceDay
          ? {
              ...deviceDay,
              sensorName:
                forBuilding.deviceDay?.sensorName ??
                defaultLegendRow?.commonName ??
                forBuilding.sensors[sf(forBuilding)][0].commonName,
              sensorUnit:
                forBuilding.deviceDay?.sensorUnit ??
                forBuilding.sensors[sf].find(
                  (it) => it.commonName === defaultLegendRow?.commonName,
                )?.sensorUnit ??
                forBuilding.sensors[sf][0].sensorUnit,
            }
          : undefined;
        forBuilding.deviceDayLegendRows =
          forBuilding.standard === STANDARD_RESET ? resetLegend : legend;
        forBuilding.deviceDayData = data;
        forBuilding.sensorChartData = data[forBuilding.deviceDay.sensorName];
      })
      .addCase(getBuilding.fulfilled, (state, action) => {
        const { meta } = action;
        const siteId = meta.arg;

        const reportMonth = moment().startOf("month");
        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          measure: MEASURE_RATING,
          reportMonth,
          standard: STANDARD[0],
          viewBy: ALL_SPACES,
        };
      })
      .addCase(getUserOptions.fulfilled, (state, action) => {
        const { payload } = action;
        state.isUseAverage = payload[USEROPTION_IAQ_USE_AVERAGE] || false;
      })
      .addCase(
        getIAQDataForBuildingAndCurrentMonth.fulfilled,
        (state, action) => {
          const { buildingData } = state;
          const { meta, payload } = action;
          const { siteId } = meta.arg;
          const data = payload.response;
          const { allSpacesWeeks, deviceWeeks, sensors, vintage } = data;
          const forBuilding = buildingData[siteId];

          forBuilding.vintage = vintage;
          forBuilding.sensors = sensors;
          forBuilding.rawAllSpacesWeeks =
            stringToDateAllSpacesWeeks(allSpacesWeeks);
          forBuilding.rawDeviceWeeks = stringToDateDeviceWeeks(deviceWeeks);

          const { measure, standard } = forBuilding;
          updateForStandardAndMeasure({
            state,
            siteId,
            measure,
            standard,
          });
        },
      )
      .addCase(initializePage.fulfilled, (state) => {
        state.status = IDLE;
      })
      .addCase(nextMonth.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(nextMonth.fulfilled, (state, action) => {
        const { meta } = action;
        const siteId = meta.arg;
        const forBuilding = state.buildingData[siteId];
        forBuilding.reportMonth = moment(
          forBuilding.reportMonth.clone().add(1, "month").format("YYYY-MM-DD"),
        );
        state.status = IDLE;
      })
      .addCase(previousMonth.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(previousMonth.fulfilled, (state, action) => {
        const { meta } = action;
        const siteId = meta.arg;
        const forBuilding = state.buildingData[siteId];
        forBuilding.reportMonth = moment(
          forBuilding.reportMonth
            .clone()
            .subtract(1, "month")
            .format("YYYY-MM-DD"),
        );
        state.status = IDLE;
      })
      .addCase(getEquipmentForBuilding.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const siteId = meta.arg;
        const equipment = payload.response;

        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          equipment,
        };
      });
  },
});

export const {
  changeDeviceDaySensor,
  changeMeasure,
  changeStandard,
  changeView,
  hideDeviceDay,
  viewForDay,
} = slice.actions;

export const INITIAL_STATE = slice.getInitialState();

export default slice.reducer;
